import "./styles.css";
import Logo from "./logo.gif";
import React, { useState, useEffect, createContext } from "react";
import SelectCase from "./SelectCase";
import Paper from "@mui/material/Paper";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  useHistory,
  useLocation,
  Link,
  Redirect,
  withRouter,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Esign from "./Esign";
import MainMenu from "./components/MainMenu";
import AdminMenu from "./components/AdminMenu";
import View from "./View";
import Register from "./Register";
import Registrations from "./components/Registrations";
import MergeOkta from "./MergeOkta";
import config from "./config";
import Navbar from "./components/Navbar";
import JSONPretty from "react-json-pretty";
import ComplianceReport from "./components/ComplianceReport";
import userMaintenance from "./components/UserMaintenance";
import EmailList from "./components/EmailList";
import CEFSContributorsDevelopers from "./components/CEFSContributorsDevelopers";

import { useQuery, useMutation } from "@apollo/react-hooks";
import { GET_USER_INFO, UPSERT_RDFS_USER } from "./Queries";

import * as Sentry from "@sentry/react";

function App() {
  const { 
    isAuthenticated, 
    user, 
    loginWithRedirect, 
    isLoading, 
    handleRedirectCallback 
  } = useAuth0();
  
  const [userInfo, setUserInfo] = useState(null);
  const history = useHistory();
  const location = useLocation();
  const homeDir = process.env.REACT_APP_HOME_DIR || "/";

  const {
    loading: userInfoLoading,
    error: userInfoError,
    data: userInfoData,
  } = useQuery(GET_USER_INFO, {
    variables: { userEmail: user?.email || null },
    fetchPolicy: "network-only",
    skip: !user // Skip the query if there's no user
  });

  const [upsertUser] = useMutation(UPSERT_RDFS_USER);

  /** 🔹 1️⃣ Process Auth0 Redirect BEFORE Login Attempt */
  useEffect(() => {
    const processAuthRedirect = async () => {
      if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
        try {
          console.log("Processing Auth0 Redirect Callback...");
          const authState = new URLSearchParams(window.location.search).get("state");
          console.log("Auth0 OAuth state (from URL):", authState);

          await handleRedirectCallback();
          history.replace(location.pathname); // ✅ Remove OAuth params after processing

          // Restore previous state after authentication
          const storedState = sessionStorage.getItem("auth_redirect_state");
          if (storedState) {
            sessionStorage.removeItem("auth_redirect_state");
            history.replace(location.pathname, JSON.parse(storedState));
          }

        } catch (error) {
          console.error("Error handling Auth0 redirect:", error);
        }
      } else {
        console.log("React Router state (location.state):", location.state);
      }
    };

    processAuthRedirect();
  }, [handleRedirectCallback, history, location]);

  /** 🔹 2️⃣ Prevent Multiple Login Redirects */
  useEffect(() => {
    const initializeAuth = async () => {
      if (!isLoading && !isAuthenticated && !window.location.search.includes("code=")) {
        console.log("User not authenticated, redirecting...");
        
        // Store state before redirecting to Auth0
        const prevState = location.state || {}; 
        sessionStorage.setItem("auth_redirect_state", JSON.stringify(prevState));

        await loginWithRedirect();
      }
    };

    initializeAuth();
  }, [isLoading, isAuthenticated, loginWithRedirect, location.state]);

  /** 🔹 3️⃣ Upsert User Info After Authentication */
  useEffect(() => {
    const syncUser = async () => {
      if (!isLoading && isAuthenticated && user) {
        console.log("Authenticated user:", user);

        try {
          setUserInfo(user);

          console.log("Upserting user:", {
            email: user.email,
            firstName: user.given_name || user.name,
            lastName: user.family_name || user.name,
          });

          await upsertUser({
            variables: {
              userEmail: user.email,
              userFrstNm: user.given_name || user.name,
              userLstNm: user.family_name || user.name,
            },
          });

          console.log("User info successfully upserted.");
        } catch (err) {
          console.error("Error updating user:", err);
        }
      }
    };

    syncUser();
  }, [isLoading, isAuthenticated, user, upsertUser]);

  if (isLoading || userInfoLoading) {
    return <div>Loading...</div>;
  }

  if (userInfoError) return `Error! ${userInfoError.message}`;

  const userSecLvl =
    userInfoData?.getUserInfo?.length > 0
      ? userInfoData.getUserInfo[0].userSecLvl
      : null;
  const allUserInfo = { ...userInfo, userSecLvl };

  if (allUserInfo.userSecLvl != null) {
    console.log("User security level:", allUserInfo.userSecLvl);
  }

  const isAdmin = allUserInfo.userSecLvl === "AD";

  const DebugRouter = ({ children }) => {
    if (process.env.REACT_APP_TEST_MODE === "true") {
      console.log(
        `Route: ${location.pathname}${location.search}, State: ${JSON.stringify(location.state)}`
      );
    }
    return children;
  };

  return (
    <UserContext.Provider value={allUserInfo}>
      <div className="App">
        {userInfo && (
          <>
            <Navbar isAdmin={isAdmin} userInfo={userInfo} />
            <br />
          </>
        )}
        <div style={{ paddingLeft: 25, paddingRight: 25 }}>
          <DebugRouter>
            <Switch>
              <Route exact path={`${homeDir}`} component={withRouter(MainMenu)} />
              <Route
                exact
                path={`${homeDir}Registrations/:status`}
                component={withRouter(Registrations)}
              />
              <Route
                exact
                path={`${homeDir}SelectCase`}
                component={withRouter(SelectCase)}
              />
              <Route
                exact
                path={`${homeDir}Register/:caseId/:step`}
                component={withRouter(Register)}
              />
              <Route
                exact
                path={`${homeDir}Register`}
                component={withRouter(SelectCase)}
              />
              <Route
                exact
                path={`${homeDir}Amend/:caseId/:step`}
                component={withRouter(Register)}
              />
              <Route
                exact
                path={`${homeDir}Esign/:caseId`}
                component={withRouter(Esign)}
              />
              <Route
                exact
                path={`${homeDir}View/:documentId`}
                component={withRouter(View)}
              />
              <Route
                exact
                path={`${homeDir}Home`}
                component={withRouter(MainMenu)}
              />
              {isAdmin ? (
                <>
                  <Route
                    exact
                    path={`${homeDir}Admin`}
                    component={withRouter(AdminMenu)}
                  />
                  <Route
                    exact
                    path={`${homeDir}Admin/compliance-report`}
                    component={withRouter(ComplianceReport)}
                  />
                  <Route
                    exact
                    path={`${homeDir}Admin/user-maintenance`}
                    component={withRouter(userMaintenance)}
                  />
                  <Route
                    exact
                    path={`${homeDir}Admin/email-list`}
                    component={withRouter(EmailList)}
                  />
                  <Route
                    exact
                    path={`${homeDir}Admin/cefs-contributors-developers`}
                    component={withRouter(CEFSContributorsDevelopers)}
                  />
                </>
              ) : (
                <Redirect to="/" />
              )}
              <Redirect from="" to="/Home" />
            </Switch>
          </DebugRouter>
        </div>
      </div>
    </UserContext.Provider>
  );
}

export const UserContext = createContext();
export default Sentry.withProfiler(App);
